import {
  acceptedDeletionReason,
  CONSENTABLE_NOTIFICATION_CHANNELS,
  campaignPeriodStatusSchema,
  campaignPricingModeSchema,
  cancelPaymentOutcomeSchema,
  currencySchema,
  DONATION_DELETE_REFUSALS,
  DONATION_DELETION_REASON_MAX_LENGTH,
  DONATION_DELETION_REASON_MIN_LENGTH,
  DONATION_EDIT_HOLDS,
  DONATION_EDIT_LOCKS,
  DONATION_EDIT_MODES,
  DONATION_EVIDENCE_MAX_BYTES,
  DONATION_EVIDENCE_RECIPIENT_ROLES,
  DONATION_HELD_FIELDS,
  DONATION_LIFECYCLE_ACTIONS,
  DONATION_NOTICE_KINDS,
  donationEntryChannelSchema,
  donationPaymentMethodSchema,
  donationStatusSchema,
  EVIDENCE_DELIVERY_BASIS_VALUES,
  EVIDENCE_LINK_REVOCATION_REASONS,
  entryLabelOverridesSchema,
  NOTIFICATION_STATUSES,
  NOTIFICATION_SUPPRESSIONS,
  paymentStatusSchema,
  queueSortKeySchema,
  staffEntryChannelSchema,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { auditEntrySchema } from './audit.js';
import { commonErrors, formErrors } from './common/errors.js';
import { formSchemaDocumentSchema } from './common/form-schema.js';
import {
  bulkResultSchema,
  bulkRowSchema,
  listRequestSchema,
  sortedListRequest,
} from './common/list.js';
import { minorAmountSchema, moneySchema } from './common/money.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { phoneSchema } from './common/phone.js';
import { queryBooleanSchema } from './common/query.js';
import { fileNameSchema, presignedUploadSchema, uploadTokenSchema } from './common/upload.js';
import { donationCampaignContract } from './donation-campaign.js';
import { donationOperationsContract } from './donation-operations.js';
import { generatedFileSchema } from './generated-file.js';
import { paymentLinkContract } from './payment-link.js';
import {
  donationPrintoutInputSchema,
  donationPrintoutListContract,
  printoutRequestResultSchema,
} from './printout-list.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

/**
 * The refusals of docs/decisions/0027-donated-at-and-backdated-entry.md. The
 * first two are a clerk correcting a date, so they are 422 beside every other
 * malformed input; the third is a permission the caller does not hold, so it is
 * 403 and audited like any other refusal.
 */
const dated = {
  ...guarded,
  DONATION_DATE_OUTSIDE_PERIOD: commonErrors.DONATION_DATE_OUTSIDE_PERIOD,
  DONATION_DATE_IN_FUTURE: commonErrors.DONATION_DATE_IN_FUTURE,
  DONATION_BACKDATE_NOT_PERMITTED: commonErrors.DONATION_BACKDATE_NOT_PERMITTED,
  PRICING_VIOLATION: commonErrors.PRICING_VIOLATION,
};

const entered = { ...dated, ...formErrors };

export const donationLifecycleActionSchema = z.enum(DONATION_LIFECYCLE_ACTIONS);

/** A reason is required wherever one is required, and it is never blank. */
const reasonSchema = z.string().trim().min(1).max(500);

/**
 * The most donations one bulk call names — and so the most the donation list
 * lets a person select across its pages, since every bulk action of the list
 * sends its whole selection.
 */
export const MAX_DONATION_BULK_IDS = 500;

const bulkIdsSchema = z.array(z.uuid()).min(1).max(MAX_DONATION_BULK_IDS);

export const donationEditabilitySchema = z.object({
  mode: z.enum(DONATION_EDIT_MODES),
  reason: z.enum(DONATION_EDIT_LOCKS).nullable(),
  /** What the work done keeps whoever corrects it — partly carried out, on a list, receipted. */
  holds: z.array(z.enum(DONATION_EDIT_HOLDS)),
  lockedAnswerKeys: z.array(z.string()),
  /** The payer, quantity, period and yurt the holds keep, beside the figures the mode keeps. */
  heldFields: z.array(z.enum(DONATION_HELD_FIELDS)),
});
export type DonationEditabilityDto = z.infer<typeof donationEditabilitySchema>;

export const donationTransitionSchema = z.object({
  action: donationLifecycleActionSchema,
  to: donationStatusSchema,
  requiresReason: z.boolean(),
});

export type DonationTransition = z.infer<typeof donationTransitionSchema>;

/**
 * One result per row, in request order. `code` names *why* as a stable English
 * identifier the client maps to Turkish; `null` is a plain success.
 */
export const donationBulkResultSchema = bulkRowSchema([
  'NOT_FOUND',
  'PERMISSION_DENIED',
  'OUT_OF_SCOPE',
  'DONATION_INVALID_TRANSITION',
  'DONATION_CANCEL_REQUIRES_REVERSAL',
  'DONATION_QUEUE_LOCKED',
  'DONATION_NOT_READY_FOR_QUEUE',
  'DONATION_CANCEL_FULLY_FULFILLED',
]);

export type DonationBulkResult = z.infer<typeof donationBulkResultSchema>;

/**
 * How many of the animals a donation's shares fall in have been carried out
 * with its shares in them, out of how many it touches — *3 hayvandan 1'i
 * kesildi*. A donation becomes `fulfilled` with the last of them
 * (docs/decisions/0025-queue-allocation-and-derived-grouping.md, amended a
 * third time 2026-09-23).
 */
export const donationAnimalsSchema = z.object({
  fulfilled: z.number().int(),
  total: z.number().int(),
});

export type DonationAnimals = z.infer<typeof donationAnimalsSchema>;

/**
 * What cancelling this donation would do, answered by the server so the cancel
 * dialog never computes it — docs/decisions/0074. `whole` is the cancellation
 * 0023 describes. `remainder` keeps the shares already recorded in a carried-out
 * animal at the unit price frozen on the donation, cancels the rest, and owes
 * back what was received beyond the kept amount, which `refund` names in the
 * donation's currency. A donation whose every share was carried out is refused
 * `DONATION_CANCEL_FULLY_FULFILLED`, as the cancel itself is.
 */
export const donationCancellationPreviewSchema = z.object({
  kind: z.enum(['whole', 'remainder']),
  shares: z.number().int(),
  recordedShares: z.number().int(),
  /** The animals its shares fall in and how many were cut, while it stands in the order. */
  animals: donationAnimalsSchema.nullable(),
  amount: moneySchema,
  keptAmount: moneySchema.nullable(),
  cancelledAmount: moneySchema.nullable(),
  refund: moneySchema.nullable(),
  paymentStatus: paymentStatusSchema,
  paymentStatusAfter: paymentStatusSchema.nullable(),
});

export type DonationCancellationPreview = z.infer<typeof donationCancellationPreviewSchema>;

/**
 * One donation holding shares in the animal `fulfillGroup` recorded: where it
 * now stands, and how many of its animals are done. A donation still waiting
 * for another of its animals answers `queued`.
 */
export const queueGroupFulfilmentResultSchema = donationBulkResultSchema.extend({
  status: donationStatusSchema,
  animals: donationAnimalsSchema,
});

export type QueueGroupFulfilmentResult = z.infer<typeof queueGroupFulfilmentResultSchema>;

const timestampSchema = z.iso.datetime({ offset: true });

/**
 * The answers to the campaign's own form. The contract cannot know their shape,
 * so it carries them opaquely and the server judges them against the version
 * the row names, through `FormSchemaCompiler.validateFormData` and nowhere else
 * — docs/decisions/0012-versioned-form-schema-compiled-to-zod.md.
 */
export const donationFormDataSchema = z.record(z.string(), z.unknown());

export const donationAttachmentSchema = z.object({
  id: z.uuid(),
  fileName: z.string(),
  contentType: z.string(),
  fileSize: z.number().int(),
  scanStatus: z.string(),
  uploadedAt: z.string(),
  /**
   * Whether the bytes may be shown: scanned clean and not deleted by retention.
   * The server decides it, so no screen infers "downloadable" from a scan
   * status whose meaning it would have to carry a copy of.
   */
  viewable: z.boolean(),
});

export type DonationAttachment = z.infer<typeof donationAttachmentSchema>;

/**
 * Where a file a donation's media area lists came from, and so which route
 * opens it: `evidence` a capture attached on the operations page
 * (`donation.attachmentDownload`), `inbound_media` a field medium matched to
 * the donation (`inboundMedia.preview`), `form_file` a file the campaign's
 * form captured (`donation.formFileDownload`), `receipt` the donation's
 * receipt PDF (`receipt.download`) — docs/decisions/0118.
 */
export const DONATION_MEDIA_SOURCES = [
  'evidence',
  'inbound_media',
  'form_file',
  'receipt',
] as const;
export const DONATION_MEDIA_KINDS = ['image', 'video', 'audio', 'document'] as const;
/**
 * Whether the bytes may be fetched: `available`, or why not — the scan has
 * not finished, it found a threat or failed, retention deleted the object, or
 * the receipt's PDF is not drawn yet.
 */
export const DONATION_MEDIA_AVAILABILITY = [
  'available',
  'pending_scan',
  'scan_refused',
  'deleted',
  'not_rendered',
] as const;

export const donationMediaItemSchema = z.object({
  id: z.uuid(),
  source: z.enum(DONATION_MEDIA_SOURCES),
  kind: z.enum(DONATION_MEDIA_KINDS),
  contentType: z.string(),
  fileName: z.string(),
  byteSize: z.number().int().nullable(),
  createdAt: z.string(),
  availability: z.enum(DONATION_MEDIA_AVAILABILITY),
  /**
   * Whether this reader holds what the source's own route asks — the server's
   * answer, so a screen never offers a link that route would refuse.
   */
  mayOpen: z.boolean(),
});

export type DonationMediaItem = z.infer<typeof donationMediaItemSchema>;

export const donationEvidenceRecipientRoleSchema = z.enum(DONATION_EVIDENCE_RECIPIENT_ROLES);

/** *KAYIT BİLDİRİMİ* or *GERÇEKLEŞTİ (GÖRSEL/KANIT) BİLDİRİMİ* — docs/decisions/0080. */
export const donationNoticeKindSchema = z.enum(DONATION_NOTICE_KINDS);

/**
 * One notice a donation sends one of its parties, with the record of who chose
 * it. It is deliberately **not** a boolean: a tick by an operator is a
 * declaration, and it is what the party is told on — the donor's per-channel
 * consent is not asked of either notice (docs/decisions/0080).
 *
 * `mayDeliver` is the server's answer, never the screen's: whether the party
 * has opted out of it.
 */
export const donationEvidenceRecipientSchema = z.object({
  role: donationEvidenceRecipientRoleSchema,
  kind: donationNoticeKindSchema,
  channel: z.enum(CONSENTABLE_NOTIFICATION_CHANNELS),
  basis: z.string(),
  declaredAt: z.string(),
  declaredByUserId: z.uuid().nullable(),
  declaredByUserName: z.string().nullable(),
  optedOutAt: z.string().nullable(),
  mayDeliver: z.boolean(),
});

export type DonationEvidenceRecipient = z.infer<typeof donationEvidenceRecipientSchema>;

/**
 * One tick of *Bildirim İşlemleri*'s grid: a party, the notice, and the
 * channel it leaves on — `DEFAULT_EVIDENCE_DELIVERY_CHANNEL` when none is
 * named. Absent from a create request, the payer is ticked for both notices
 * and nobody else for either — `DEFAULT_NOTICE_CHOICES`, *Varsayılan Durum*.
 */
export const donationNoticeRecipientInputSchema = z.object({
  role: donationEvidenceRecipientRoleSchema,
  kind: donationNoticeKindSchema,
  channel: z.enum(CONSENTABLE_NOTIFICATION_CHANNELS).optional(),
});

/**
 * Where one party's evidence link stands. `not_offered` has no link row yet;
 * `unissued` was offered and holds no token — its message is still to leave,
 * was withheld, or certainly failed and gave the link back; `sent` holds a
 * token that opens until `expiresAt`; `expired` held one past it; `revoked`
 * stopped for good, for the reason on the row —
 * docs/decisions/0075-evidence-travels-as-an-expiring-link.md.
 */
export const DONATION_EVIDENCE_LINK_STATES = [
  'not_offered',
  'unissued',
  'sent',
  'expired',
  'revoked',
] as const;

/**
 * **One party's evidence delivery as staff read it on the donation page** —
 * who asked for it and on what basis, whether it may leave today, and where its
 * link stands. It carries **no token and no hash**, and the destination only
 * masked, as the donor screens show one: the page is read by staff who have no
 * reason to be handed a third party's number.
 *
 * `actions` is what the row allows, judged as the writes judge it — `resend`
 * mirrors every refusal of `donation.evidenceResend`, `optOut` a row not yet
 * opted out. It says nothing of the reader's grants: the screen narrows it by
 * the permission each route checks.
 */
export const donationEvidenceDeliverySchema = z.object({
  role: donationEvidenceRecipientRoleSchema,
  channel: z.enum(CONSENTABLE_NOTIFICATION_CHANNELS),
  basis: z.enum(EVIDENCE_DELIVERY_BASIS_VALUES),
  declaredAt: z.string(),
  declaredByUserName: z.string().nullable(),
  optedOutAt: z.string().nullable(),
  mayDeliver: z.boolean(),
  destinationMasked: z.string().nullable(),
  link: z.object({
    state: z.enum(DONATION_EVIDENCE_LINK_STATES),
    issuedAt: z.string().nullable(),
    expiresAt: z.string().nullable(),
    openCount: z.number().int().nonnegative(),
    lastOpenedAt: z.string().nullable(),
    revokedAt: z.string().nullable(),
    revokedReason: z.enum(EVIDENCE_LINK_REVOCATION_REASONS).nullable(),
    /** How many messages ever carried this link; more than one means every earlier token stopped opening. */
    messages: z.number().int().nonnegative(),
  }),
  lastMessage: z
    .object({
      status: z.enum(NOTIFICATION_STATUSES),
      queuedAt: z.string(),
      settledAt: z.string().nullable(),
      suppressionReason: z.enum(NOTIFICATION_SUPPRESSIONS).nullable(),
    })
    .nullable(),
  actions: z.object({ resend: z.boolean(), optOut: z.boolean() }),
});

export type DonationEvidenceDelivery = z.infer<typeof donationEvidenceDeliverySchema>;

/**
 * **One party's row of *Bildirim İşlemleri* as the donation page shows it** —
 * docs/decisions/0080. `named` is whether the donation names this party at all
 * (a reference with a telephone, *Bağış Sahibi* as a donor); `reachable`
 * whether that party has a telephone a message could go to. A party that is
 * not named cannot be ticked; one that is not reachable can, and is written
 * down `no_destination` when its message is due.
 *
 * `registration` went out once, when the donation was saved, so it is shown —
 * whether it was ticked and what became of its message — and never edited.
 * `fulfilment` is edited until its link is offered: `offered` says it was, and
 * from then on the row's own opt-out is the way to stop it.
 */
export const donationNoticePartySchema = z.object({
  role: donationEvidenceRecipientRoleSchema,
  named: z.boolean(),
  reachable: z.boolean(),
  registration: z.object({
    chosen: z.boolean(),
    optedOutAt: z.string().nullable(),
    /** The newest registration message for the party, or null when none was written. */
    status: z.enum(NOTIFICATION_STATUSES).nullable(),
    suppressionReason: z.enum(NOTIFICATION_SUPPRESSIONS).nullable(),
  }),
  fulfilment: z.object({
    chosen: z.boolean(),
    optedOutAt: z.string().nullable(),
    offered: z.boolean(),
  }),
});

export type DonationNoticeParty = z.infer<typeof donationNoticePartySchema>;

export const donationNoticeChoicesSchema = z.object({
  parties: z.array(donationNoticePartySchema),
  /** False for a cancelled donation, whose notices nobody may change any more. */
  editable: z.boolean(),
});

export type DonationNoticeChoices = z.infer<typeof donationNoticeChoicesSchema>;
export type DonationEvidenceLinkState = (typeof DONATION_EVIDENCE_LINK_STATES)[number];

/**
 * An organization the donation's institution is linked to, any of which may
 * take its money — docs/decisions/0112. The primary is the one that speaks for
 * the donation until then.
 */
export const donationEligibleOrganizationSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  isPrimary: z.boolean(),
});

export type DonationEligibleOrganization = z.infer<typeof donationEligibleOrganizationSchema>;

export const donationSchema = z.object({
  id: z.uuid(),
  referenceCode: z.string(),
  campaignId: z.uuid(),
  campaignCode: z.string(),
  campaignName: z.string(),
  pricingMode: campaignPricingModeSchema,
  unitLabel: z.string().nullable(),
  /**
   * The campaign's own overrides of the entry form's section headers and
   * party labels, denormalized here so the notice grid and the party section
   * of this donation's own page show the same labels the entry form did —
   * docs/plans/11-test-revisions.md §11.7.
   */
  entryLabels: entryLabelOverridesSchema,
  campaignPeriodId: z.uuid(),
  seasonYear: z.number().int().nullable(),
  periodStatus: campaignPeriodStatusSchema,
  campaignFormVersion: z.number().int(),
  formData: donationFormDataSchema,
  institutionId: z.uuid(),
  institutionName: z.string(),
  countryId: z.uuid(),
  countryName: z.string(),
  zoneId: z.uuid(),
  zoneName: z.string(),
  regionId: z.uuid(),
  regionName: z.string(),
  /**
   * The organization that took the donation's money — null until a payment is
   * matched or captured, and for a portal donation never (the site's
   * organization took it at checkout). docs/decisions/0112 §2.
   */
  organizationId: z.uuid().nullable(),
  organizationName: z.string().nullable(),
  /**
   * **The main organization** (*ana kuruluş*, docs/decisions/0128): the
   * organization the donation is mainly paid through — `organizationId` once
   * money decided it, else the primary organization of the institution that
   * recorded it. Null only for a donation whose institution has no primary
   * organization. `organizationSetManually` says an upper authority chose it.
   */
  mainOrganizationId: z.uuid().nullable(),
  mainOrganizationName: z.string().nullable(),
  organizationSetManually: z.boolean(),
  donorId: z.uuid().nullable(),
  donorName: z.string().nullable(),
  /** The payer's telephone, **masked** like every other party's; correcting it means typing it again. */
  donorPhoneMasked: z.string().nullable(),
  donorIsAnonymous: z.boolean(),
  amount: moneySchema,
  quantity: z.number().int().nullable(),
  unitAmount: moneySchema.nullable(),
  baseAmount: moneySchema.nullable(),
  exchangeRateId: z.uuid().nullable(),
  paymentMethod: donationPaymentMethodSchema,
  paymentReference: z.string().nullable(),
  entryChannel: donationEntryChannelSchema,
  /** Two axes that move independently, and no screen may imply one is the other. */
  status: donationStatusSchema,
  paymentStatus: paymentStatusSchema,
  /**
   * Whether the donation's own answers meet its campaign's readiness condition
   * — `isReadyForQueue` in `packages/core`, answered by the server. A
   * `pending` donation that is not ready is listed under *Tümü* with a marker
   * rather than under *Bekleyenler*, and `donation.queue` refuses it —
   * docs/decisions/0077.
   */
  readyForQueue: z.boolean(),
  donatedAt: z.string(),
  submittedAt: z.string().nullable(),
  queuePosition: z.number().int().nullable(),
  queuedAt: z.string().nullable(),
  /** Where the work is *planned*, against where it was actually carried out. */
  fulfillmentInstitutionId: z.uuid().nullable(),
  fulfillmentInstitutionName: z.string().nullable(),
  fulfilledInstitutionId: z.uuid().nullable(),
  fulfilledInstitutionName: z.string().nullable(),
  fulfilledAt: z.string().nullable(),
  /** In its own currency: a cost spent in TRY against a campaign priced in EUR is ordinary. */
  fulfillmentCost: moneySchema.nullable(),
  /**
   * The name on the evidence image; null prints no name. Not
   * `campaigns.beneficiaryRefFieldKey`, which is a sponsorship matching key —
   * docs/decisions/0070, and docs/decisions/0079 for why there is no *Anonim*.
   */
  onBehalfOfName: z.string().nullable(),
  /**
   * *Bağış Sahibi* as a person: the donor the dedication's telephone resolved
   * to, and that donor's telephone **masked**, as the reference's is. Showing
   * it in full is the entry picker's job, asked of `donor.get` for one donor
   * the reader may read — docs/decisions/0079.
   */
  onBehalfOfDonorId: z.uuid().nullable(),
  onBehalfOfPhoneMasked: z.string().nullable(),
  referenceName: z.string().nullable(),
  /**
   * **Masked**, deliberately, and named so nobody echoes it back into a patch.
   * A reference person is a third party whose number this screen never has to
   * show in full; correcting it means typing it again.
   */
  referencePhoneMasked: z.string().nullable(),
  note: z.string().nullable(),
  /** Null where nobody typed it: a portal order a verified capture turned into a donation. */
  createdByUserId: z.uuid().nullable(),
  createdByUserName: z.string().nullable(),
  /**
   * *Kaydı Oluşturan*: who actually typed the donation — the session user at
   * creation, which differs from the operator above when it was recorded for
   * somebody else (docs/decisions/0070). Null exactly when the operator is.
   */
  recordedByUserId: z.uuid().nullable(),
  recordedByUserName: z.string().nullable(),
  /**
   * *Kendi Kurumu Olarak*: the yurt is the institution that recorded the
   * donation. Derived — the two ids are equal — never stored
   * (docs/decisions/0123).
   */
  recordedAsOwnInstitution: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type Donation = z.infer<typeof donationSchema>;

/**
 * A donation in the trash: the row as it was, with who took it off the books,
 * when and why. Its `status` is the one a restore puts it back in —
 * docs/decisions/0078-a-deleted-donation-is-kept-and-restored.md.
 */
export const deletedDonationSchema = donationSchema.extend({
  deletedAt: z.string(),
  deletedByUserId: z.uuid(),
  deletedByUserName: z.string().nullable(),
  /** Null only on a row deleted before docs/decisions/0085 made the reason required. */
  deletionReason: z.string().nullable(),
  /** The bulk deletion the row left in, shared by all its rows; null for a single deletion. */
  bulkOperationId: z.uuid().nullable(),
});

export type DeletedDonation = z.infer<typeof deletedDonationSchema>;

/**
 * One result per row of a bulk deletion, in request order: the refusals a
 * single deletion throws, named per row — docs/decisions/0078.
 */
export const donationDeletionBulkResultSchema = bulkRowSchema([
  'NOT_FOUND',
  'PERMISSION_DENIED',
  'OUT_OF_SCOPE',
  ...DONATION_DELETE_REFUSALS,
]);

export type DonationDeletionBulkResult = z.infer<typeof donationDeletionBulkResultSchema>;

/** The trash is newest first; `reference_code` is the tiebreaker, as on the list. */
export const DELETED_DONATION_SORT_FIELDS = [
  'deletedAt',
  'donatedAt',
  'amountMinor',
  'referenceCode',
] as const;

export type DeletedDonationSortField = (typeof DELETED_DONATION_SORT_FIELDS)[number];

export const deletedDonationListInputSchema = sortedListRequest(
  DELETED_DONATION_SORT_FIELDS,
  'deletedAt',
  'desc',
).extend({
  campaignId: z.uuid().optional(),
  /** The rows one bulk deletion took into the trash — docs/decisions/0085. */
  bulkOperationId: z.uuid().optional(),
});

export type DeletedDonationListInput = z.infer<typeof deletedDonationListInputSchema>;

/**
 * Required, on a single and a bulk deletion alike: at least ten characters
 * once trimmed, counted as the database counts them — docs/decisions/0085,
 * superseding 0078's optional reason. The service asks the same rule again,
 * and a deletion made since migration `0119` cannot be stored without it.
 */
const deletionReasonSchema = z
  .string()
  .trim()
  .min(DONATION_DELETION_REASON_MIN_LENGTH)
  .max(DONATION_DELETION_REASON_MAX_LENGTH)
  .refine((reason) => acceptedDeletionReason(reason) !== null, {
    message: `A deletion needs a reason of at least ${DONATION_DELETION_REASON_MIN_LENGTH} characters`,
  });

/** A bulk deletion's answer: a result per row, and the one id every deleted row carries. */
export const donationDeletionBulkResponseSchema = bulkResultSchema(
  donationDeletionBulkResultSchema,
).extend({
  /** Written on every row the call deleted and into each row's audit entry — docs/decisions/0085. */
  bulkOperationId: z.uuid(),
});

export type DonationDeletionBulkResponse = z.infer<typeof donationDeletionBulkResponseSchema>;

/**
 * A deleted donation's own page, read-only: the record as it was, the form it
 * was captured under, and the deletion — docs/decisions/0085. It has no
 * attachments to show, because a donation evidence reached is never deleted.
 */
export const deletedDonationDetailSchema = z.object({
  donation: deletedDonationSchema,
  formSchema: formSchemaDocumentSchema,
});

export type DeletedDonationDetail = z.infer<typeof deletedDonationDetailSchema>;

/**
 * The fields a clerk fills in. They carry no defaults, because `update` is a
 * patch: a default on an absent key would overwrite what is already stored
 * every time the screen sends one field.
 */
const donationEntrySchema = z.object({
  donorId: z.uuid().nullable(),
  /** Defaults to the server clock; a client may send a past day it can justify. */
  donatedAt: timestampSchema,
  currency: currencySchema,
  quantity: z.number().int().min(1),
  /**
   * Where the work will be carried out, chosen at entry since §10.5 because the
   * yurt decides the price: it must be an active assignment of the donation's
   * period, and the amount is frozen against its override —
   * docs/decisions/0068-a-price-may-be-overridden-per-assignment.md.
   */
  fulfillmentInstitutionId: z.uuid().nullable(),
  /** Ignored for a `unit` campaign, whose amount is the unit price times the quantity. */
  amountMinor: minorAmountSchema,
  paymentMethod: donationPaymentMethodSchema,
  paymentReference: z.string().trim().max(120).nullable(),
  /** The evidence image's dedication; `null` prints no name — docs/decisions/0079. */
  onBehalfOfName: z.string().trim().max(200).nullable(),
  /**
   * *Bağış Sahibi* as a person, named the two ways the payer is: a donor the
   * clerk chose, or a telephone the server resolves by 0067's rule — an exact
   * normalised match in the caller's scope attaches, none creates a donor, two
   * are refused. Never both, and either needs the name above; a name alone is
   * a dedication to nobody on file. A correction sending either, or clearing
   * the name, replaces the person as a whole — docs/decisions/0079.
   */
  onBehalfOfDonorId: z.uuid().nullable(),
  onBehalfOfPhone: phoneSchema.nullable(),
  /**
   * A name alone is a reminder of who introduced the donor and reaches nobody;
   * a telephone without a name is refused, by the server and by the database.
   */
  referenceName: z.string().trim().max(200).nullable(),
  referencePhone: phoneSchema.nullable(),
  note: z.string().trim().max(2000).nullable(),
  formData: donationFormDataSchema,
});

/**
 * One row of a period's queue, with its derived grouping. The group is a window
 * over the order and has no row of its own: `groupFrom`, `groupTo` and the
 * share count in each come from `campaigns.units_per_group` applied to
 * `queue_position` at read time — docs/decisions/0018-generic-fulfillment-queue.md.
 */
export const queueEntrySchema = z.object({
  id: z.uuid(),
  referenceCode: z.string(),
  donorName: z.string().nullable(),
  status: donationStatusSchema,
  queuePosition: z.number().int(),
  shares: z.number().int(),
  sharesBefore: z.number().int(),
  groupFrom: z.number().int().nullable(),
  groupTo: z.number().int().nullable(),
  /**
   * The share's part in each group it falls in, and how many shares make that
   * group's animal — its answer's own size, or `unitsPerGroup`
   * (docs/decisions/0072-a-price-may-depend-on-the-answers-and-a-group-on-the-animal.md).
   */
  groupShares: z.array(
    z.object({
      group: z.number().int(),
      shares: z.number().int(),
      size: z.number().int(),
      /** The animal was carried out with this donation's shares in it. */
      fulfilled: z.boolean(),
    }),
  ),
  /**
   * The share's answers to the campaign's grouping fields, in
   * `period.groupingFields` order: an option value, `true` for a ticked box,
   * and null for no answer or an unticked box — the equality the grouping
   * compares by, so two shares of one group read the same here.
   */
  groupingAnswers: z.array(z.union([z.string(), z.number(), z.literal(true)]).nullable()),
  amount: moneySchema,
  institutionId: z.uuid(),
  institutionName: z.string(),
  countryName: z.string(),
  zoneName: z.string(),
  fulfillmentInstitutionId: z.uuid().nullable(),
  fulfillmentInstitutionName: z.string().nullable(),
  fulfilledInstitutionName: z.string().nullable(),
  fulfilledAt: z.string().nullable(),
  fulfillmentCost: moneySchema.nullable(),
  /** The evidence gap is a query: this is that query, per row. */
  attachmentCount: z.number().int(),
});

export type QueueEntry = z.infer<typeof queueEntrySchema>;

export const queuePeriodSchema = z.object({
  campaignPeriodId: z.uuid(),
  campaignId: z.uuid(),
  campaignName: z.string(),
  seasonYear: z.number().int().nullable(),
  unitLabel: z.string().nullable(),
  /** Null collapses the derivation to a flat list, which is every campaign that is not shares. */
  unitsPerGroup: z.number().int().nullable(),
  totalShares: z.number().int(),
  groupCount: z.number().int(),
  fulfilledCount: z.number().int(),
  /** The animals carried out — `fulfilled_queue_groups` rows of the period. */
  fulfilledGroupCount: z.number().int(),
  /**
   * Reordering executed work rewrites history, so one fulfilled row or one
   * carried-out animal closes it.
   */
  reorderable: z.boolean(),
  /**
   * The groups holding fewer shares than make their animal — an animal not yet
   * full. With grouping answers named, a group ends where the answers change,
   * so a short group may stand anywhere in the order rather than only last.
   */
  shortGroups: z.array(z.number().int()),
  /**
   * The grouping answer whose options carry their own group size, and each
   * sized option with its size — a sheep 1, a camel share 7. Null when every
   * group is `unitsPerGroup` shares.
   */
  groupSizing: z
    .object({
      fieldKey: z.string(),
      label: z.string(),
      sizes: z.array(z.object({ value: z.string(), label: z.string(), size: z.number().int() })),
    })
    .nullable(),
  /**
   * What the shares of one group must agree on besides the yurt — the
   * campaign's `group_by_field_keys`, labelled from its published schema. Empty
   * is the plain window of every `unitsPerGroup` consecutive shares.
   */
  groupingFields: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      type: z.enum(['select', 'radio', 'checkbox']).nullable(),
      options: z.array(z.object({ value: z.string(), label: z.string() })),
    }),
  ),
});

export type QueuePeriod = z.infer<typeof queuePeriodSchema>;

/**
 * The money a fulfillment cost, in its own currency. Optional because a
 * fulfillment that cost the organization nothing is ordinary.
 */
const fulfillmentInputSchema = z.object({
  fulfilledInstitutionId: z.uuid().optional(),
  fulfilledAt: timestampSchema.optional(),
  cost: moneySchema.optional(),
});

/**
 * The key an offline client minted when the person acted. The field app queues
 * what was done where there is no signal and sends it when the connection
 * returns, so the same action arrives twice whenever a reply is lost on the way
 * back. The key makes the second arrival change nothing — without it the second
 * is a second fulfilment, with a second audit row and a second
 * `fulfillment_cost` delta against the period's totals.
 */
const clientRequestIdSchema = z.uuid().optional();

/** One row of a bulk completion, each answering for itself and each replay-safe on its own key. */
export const bulkFulfilmentItemsSchema = z
  .array(z.object({ id: z.uuid(), clientRequestId: clientRequestIdSchema }))
  .min(1)
  .max(500)
  .refine(
    (items) => new Set(items.map((item) => item.id)).size === items.length,
    'a donation appears once in one request',
  );

/**
 * `reference_code` is unique, so it is both a sortable column and the
 * tiebreaker every other ordering ends with — without one, two rows sharing a
 * `donated_at` can swap between pages and a paged sort is not a sort.
 */
export const DONATION_SORT_FIELDS = [
  'donatedAt',
  'amountMinor',
  'referenceCode',
  'createdAt',
] as const;

export type DonationSortField = (typeof DONATION_SORT_FIELDS)[number];

export const donationListInputSchema = sortedListRequest(
  DONATION_SORT_FIELDS,
  'donatedAt',
  'desc',
).extend({
  /**
   * The whole campaign, every season of it. It is what the sidebar's campaign
   * link filters on, because the menu names a campaign rather than a season —
   * docs/plans/10-operational-redesign.md §10.2.
   */
  campaignId: z.uuid().optional(),
  /**
   * One season. Given together with `campaignId` the two **intersect**: both
   * are ANDed, like every other id on this input, so a period belonging to
   * another campaign matches no rows rather than one of the pair winning. A
   * donation's campaign is derived from its period and the database holds them
   * in step (`donations_campaign_matches_period`), so the intersection is
   * either the period or nothing — never a contradiction the caller cannot see.
   */
  campaignPeriodId: z.uuid().optional(),
  donorId: z.uuid().optional(),
  /**
   * Every donor of this donor's **merge group** — the survivor and every donor
   * merged into it, however deep — by `root_donor_id`. A merge moves no
   * donation, so this is what a surviving donor's *Kalan borç* card links to
   * for the group's figures (docs/decisions/0102 §3, amended). It never widens
   * a caller: ANDed with the scope predicate like every other id here.
   */
  mergeGroupDonorId: z.uuid().optional(),
  /** *Kayıt Eden Kurum*: the institution that recorded the donation. */
  institutionId: z.uuid().optional(),
  /**
   * One zone, one country: matched on the donation's own denormalized ancestor
   * columns and ANDed with every other filter, so — unlike `scopeLevel`, which
   * names a single node — a zone, a country and an institution narrow
   * together. A node outside the caller's scope matches no rows.
   */
  zoneId: z.uuid().optional(),
  countryId: z.uuid().optional(),
  /**
   * *Personel*: the user the donation is attributed to — the operator it is
   * recorded for, which a template's *Kaydeden kişi* column names — and not
   * `recordedByUserId`, who typed it (docs/decisions/0070, 0134).
   */
  operatorUserId: z.uuid().optional(),
  /**
   * *Kaydı Oluşturan*: the user who typed the donation (`recorded_by_user_id`).
   * Like every id here it is ANDed with the scope predicate, so it never
   * reaches a row the caller could not already read (docs/decisions/0123).
   */
  recordedByUserId: z.uuid().optional(),
  /**
   * *Kendi Kurumu Olarak*: `true` lists the donations whose yurt is their
   * recording institution, `false` every other — one naming another yurt or
   * none (docs/decisions/0123).
   */
  recordedAsOwnInstitution: queryBooleanSchema.optional(),
  status: donationStatusSchema.optional(),
  paymentStatus: paymentStatusSchema.optional(),
  currency: currencySchema.optional(),
  /** Sorted and filtered on `donated_at`, never `created_at`. */
  donatedFrom: timestampSchema.optional(),
  donatedTo: timestampSchema.optional(),
  minAmountMinor: minorAmountSchema.optional(),
  maxAmountMinor: minorAmountSchema.optional(),
  /**
   * *Gerçekleştirilecek Kurum*: where the work is planned, which is what the
   * fulfillment inbox is filtered on.
   */
  fulfillmentInstitutionId: z.uuid().optional(),
  /**
   * The fulfilment inbox: the work routed to one institution. Two ways in — the
   * donation names the place, or the place holds an active
   * `campaign_assignments` row for the period and the donation names nobody yet
   * — so the inbox is a saved filter on this list rather than a screen of its
   * own, exactly as *Bekleyenler* is.
   *
   * It never widens a caller, like every other id on this input: the predicate
   * is ANDed with the scope predicate, so naming another branch's institution
   * shows only the rows the caller could already see.
   */
  fulfillmentInboxInstitutionId: z.uuid().optional(),
  /**
   * The campaign's category, not the donation's — a donation has none. It is a
   * filter rather than a second list route because the answer is rows of this
   * list, and §4.2's saved views want it as an input anyway.
   */
  categoryId: z.uuid().optional(),
  /**
   * One node of the hierarchy, named by its level and matched against the
   * donation's own denormalized ancestor column. Both halves are needed and a
   * half-set pair filters nothing: this stays a plain object because
   * `donationExportInputSchema` omits from it, and a cross-field `.refine`
   * would make it a `ZodEffects` that cannot be omitted from.
   *
   * It never widens a caller. The predicate is ANDed with the scope predicate,
   * so a node outside the caller's scope matches no rows rather than reaching
   * any.
   */
  scopeLevel: z.enum(['region', 'zone', 'country', 'institution']).optional(),
  scopeId: z.uuid().optional(),
  /**
   * The evidence gap, as a query rather than a state: fulfilled rows with
   * no attachment. Attaching evidence never moved a status, so the report
   * cannot be a status — docs/decisions/0025-queue-allocation-and-derived-grouping.md.
   */
  missingEvidence: queryBooleanSchema.optional(),
  /**
   * Whether evidence was received: an attachment on the donation, or an
   * inbound medium matched to it. `false` asks for the donations with
   * none. Evidence never moves a status, so this is a filter a person
   * works from — docs/plans/05-finance-reconciliation.md §5.9.
   */
  hasEvidence: queryBooleanSchema.optional(),
  /** Pending or queued: the donations a field team still has to carry out. */
  awaitingFulfilment: queryBooleanSchema.optional(),
  /**
   * *Kalan borcu olanlar*: the donations that still owe money — not cancelled,
   * and unpaid, partly settled or reversed, the payment states that can take a
   * match. What a donor's outstanding card links to, and where a basket is
   * filled from.
   */
  owing: queryBooleanSchema.optional(),
  /**
   * *Bekleyenler*: `pending` donations that are ready for the queue — their
   * campaign names no readiness field, or their answer to the one it names is
   * `true`. A filter rather than a status, for the reason the fulfilment inbox
   * is — the answer is rows of this list — docs/decisions/0077, amending
   * docs/decisions/0071-a-ready-donation-waits-and-the-order-locks-it.md.
   */
  waitingForQueue: queryBooleanSchema.optional(),
  /**
   * Where the work is carried out — the yurt, falling back to the entry
   * institution. It never widens a caller: it is ANDed with what the caller
   * reads, so it is the institution selector of a person staffed at more than
   * one (docs/decisions/0090).
   */
  workInstitutionId: z.uuid().optional(),
  /**
   * Only the donations the caller's in-force campaign staff assignments reach
   * — their campaign, worked at their institution. A caller with none gets
   * none (docs/decisions/0090).
   */
  assignedToMe: queryBooleanSchema.optional(),
});

export type DonationListInput = z.infer<typeof donationListInputSchema>;

/**
 * The filter a donation export is generated from: the list's own, without its
 * page. An export is every matching row, so a page number would be a lie —
 * docs/decisions/0043-the-worker.md §7. The ordering stays, so the file reads
 * in the order the screen was showing.
 */
export const donationExportInputSchema = donationListInputSchema.omit({
  page: true,
  pageSize: true,
});

export type DonationExportInput = z.infer<typeof donationExportInputSchema>;

export const queueSheetInputSchema = z.object({ campaignPeriodId: z.uuid() });

/**
 * §4.3's Excel kesim report: a period's order as a spreadsheet, a row per
 * donation per group, read through the requester's scope when the job runs.
 */
export const queueExportInputSchema = z.object({ campaignPeriodId: z.uuid() });

/**
 * The payer as a clerk dictates them: a name and a telephone, and nothing else
 * to fill in — docs/decisions/0067-a-donation-resolves-its-donor-and-only-a-telephone-decides.md.
 * The telephone arrives in E.164, its country chosen on the screen
 * (docs/decisions/0086); `countryIso` is kept for a caller that still sends
 * it and changes nothing about a number already international.
 */
export const inlinePayerSchema = z.object({
  name: z.string().trim().min(2).max(200),
  phone: phoneSchema,
  countryIso: z
    .string()
    .trim()
    .length(2)
    .transform((value) => value.toUpperCase())
    .optional(),
});

export type InlinePayer = z.infer<typeof inlinePayerSchema>;

export type QueueSheetInput = z.infer<typeof queueSheetInputSchema>;

/**
 * Everything a staff entry names but the payer. The payer is the union below,
 * so the two ways of naming one cannot both be sent.
 */
const donationCreateFieldsSchema = donationEntrySchema
  .partial()
  .omit({ donorId: true })
  .extend({
    /**
     * **Both optional, and at least one required** — the refine below. §3.2 took
     * the period picker off the form, so the wizard names the campaign and the
     * server resolves that campaign's own active period; naming the period
     * outright is still accepted, and is the only way to enter into a `closed`
     * season inside its backdating window. Naming both is accepted and refused
     * if they disagree; what is stored is always the period row's own
     * `campaign_id` — docs/decisions/0070.
     */
    campaignPeriodId: z.uuid().optional(),
    campaignId: z.uuid().optional(),
    /**
     * Typed as every currency so the shape stays the shared one, but staff
     * enter only `STAFF_ENTRY_CURRENCIES` — TRY, USD, EUR — and anything else
     * is refused `DONATION_ENTRY_CURRENCY_UNSUPPORTED` by the server, whatever
     * client sent it. `update` refuses a move into another; keeping a stored
     * one is not a move — docs/decisions/0070.
     */
    currency: currencySchema,
    /** Absent means `unspecified`, which is not `other` — docs/decisions/0070. */
    paymentMethod: donationPaymentMethodSchema.optional(),
    /**
     * Whom the entry is recorded against. Absent, it is the caller. Naming
     * anybody else needs `donation.recordForOperator` and passes the privilege
     * ceiling's rules 1 and 3, exactly as designating a campaign manager does.
     */
    enteredByUserId: z.uuid().optional(),
    /**
     * *Bildirim İşlemleri*: who is told the donation was recorded and who is
     * told it was carried out. Absent ticks the payer for both — the default;
     * an empty list ticks nobody — docs/decisions/0080.
     */
    noticeRecipients: z.array(donationNoticeRecipientInputSchema).max(8).optional(),
    formData: donationFormDataSchema.default({}),
    /**
     * Which staff desk typed it. Only a staff channel is offered, so a client
     * cannot claim a row came from the portal, the field app, a box, a pledge or
     * an import.
     */
    entryChannel: staffEntryChannelSchema.optional(),
    /** The idempotency key of an offline field sync; never reused by the admin. */
    clientRequestId: z.uuid().optional(),
  });

/**
 * **Exactly one of the two, as a union rather than two nullable fields and a
 * check.** A request naming a `donorId` *and* a `payer` matches neither branch
 * and is refused at the boundary, and the inferred input type is one or the
 * other — so a caller that sends both, or neither, does not type-check either.
 * Two optional fields with a `.refine` would accept both shapes as far as
 * TypeScript is concerned and leave the service to decide which one won.
 *
 * `donorId: z.null()` on the payer branch is there so an admin screen that
 * clears its picked candidate may send the field as null rather than having to
 * delete it — docs/decisions/0067-a-donation-resolves-its-donor-and-only-a-telephone-decides.md.
 */
const donationCreateInputSchema = z
  .union([
    donationCreateFieldsSchema.extend({
      donorId: z.uuid(),
      payer: z.undefined().optional(),
    }),
    donationCreateFieldsSchema.extend({
      donorId: z.null().optional(),
      payer: inlinePayerSchema,
    }),
  ])
  .refine((input) => input.campaignPeriodId !== undefined || input.campaignId !== undefined, {
    message: 'a donation names either its campaign or one of its periods',
  });

export type DonationCreateInput = z.infer<typeof donationCreateInputSchema>;

/**
 * A user the entry form may offer as the operator a donation is recorded
 * against. `manageable` is the privilege ceiling's answer: false for a user
 * holding a permission the caller does not, who is listed so the picker can
 * say why it is locked, and whom `donation.create` would refuse.
 */
export const donationOperatorOptionsInputSchema = listRequestSchema
  .extend({
    scopeLevel: z.enum(['zone', 'country', 'institution']).optional(),
    scopeId: z.uuid().optional(),
  })
  .refine((input) => (input.scopeLevel === undefined) === (input.scopeId === undefined), {
    message: 'A recorder filter needs both its level and node',
    path: ['scopeId'],
  });

export const donationOperatorOptionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  email: z.string(),
  manageable: z.boolean(),
  /** Whether a telephone is held for them — *Bildirim İşlemleri*'s operator row asks it (docs/decisions/0080). */
  hasPhone: z.boolean(),
});

export type DonationOperatorOption = z.infer<typeof donationOperatorOptionSchema>;

export const donationContract = {
  /**
   * The donation's hosted-checkout link, nested under the donation because
   * that is what it belongs to — docs/plans/06-portal-and-field.md §6.2.
   */
  paymentLink: paymentLinkContract,

  /**
   * What the donation screens read about campaigns, behind their own
   * permissions rather than a campaign one — docs/decisions/0088.
   */
  campaigns: donationCampaignContract,

  /** The campaign operations pages — docs/decisions/0091. */
  ...donationOperationsContract,

  /** PDF management — docs/decisions/0092. */
  ...donationPrintoutListContract,

  list: oc
    .route({ method: 'GET', path: '/donations', summary: 'Donations inside the caller’s scope' })
    .errors(guarded)
    .input(donationListInputSchema)
    .output(paginated(donationSchema)),

  /**
   * §4.8's printouts: a per-record page for each donation named, as one PDF the
   * worker draws. The request makes a **PDF list** of the donations it names —
   * refused while any of them is on an active list — in the same transaction
   * as the file's row (docs/decisions/0092). The job re-authorizes the
   * requester, reads the list through their reach, and sets `printed_at` with
   * a `donation` / `print` row per record in the same transaction.
   */
  print: oc
    .route({ method: 'POST', path: '/donations/printouts' })
    .errors(guarded)
    .input(donationPrintoutInputSchema)
    .output(printoutRequestResultSchema),

  /**
   * §4.8's group sheet: the period's queue in `units_per_group` blocks. The
   * sheet is titled from the derivation — a group has no name because it has no
   * row.
   */
  queueSheet: oc
    .route({ method: 'POST', path: '/donations/queue/sheet' })
    .errors(guarded)
    .input(queueSheetInputSchema)
    .output(generatedFileSchema),

  /**
   * §4.3's kesim report as a real `.xlsx`: every group's *kesim sıra no*, its
   * shares with their reference codes, the donor, the name it is given on
   * behalf of, the yurt and every grouping answer. A generated file like every
   * export — `donation.export` beside `donation.queue`, checked at the request
   * and again when the job runs.
   */
  queueExport: oc
    .route({ method: 'POST', path: '/donations/queue/export' })
    .errors(guarded)
    .input(queueExportInputSchema)
    .output(generatedFileSchema),

  /**
   * Asks the worker for the whole filter as a file. Always a job and never a
   * synchronous download, whatever the row count — docs/decisions/0043-the-worker.md
   * §7. The answer is the `generated_files` row, which the screen then watches.
   */
  export: oc
    .route({ method: 'POST', path: '/donations/export' })
    .errors(guarded)
    .input(donationExportInputSchema)
    .output(generatedFileSchema),

  recordingInstitutionOptions: oc
    .route({ method: 'GET', path: '/donations/recording-institution-options' })
    .errors(guarded)
    .input(listRequestSchema)
    .output(paginated(z.object({ id: z.uuid(), name: z.string() }))),

  /**
   * §3.2's *Yetkili kullanıcılar düzenleyebilir*: the active users in the
   * caller's scope, for the entry form's operator picker. Behind
   * `donation.recordForOperator` rather than `user.list`, because it is the
   * entry form's own question — a reader allowed to name an operator must be
   * able to see whom, whether or not they may open the users screen. The
   * `manageable` flag is the same ceiling `donation.create` applies.
   */
  operatorOptions: oc
    .route({ method: 'GET', path: '/donations/operator-options' })
    .errors(guarded)
    .input(donationOperatorOptionsInputSchema)
    .output(paginated(donationOperatorOptionSchema)),

  get: oc
    .route({ method: 'GET', path: '/donations/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(
      z.object({
        donation: donationSchema,
        /** The schema the answers were captured under, never the campaign's current one. */
        formSchema: formSchemaDocumentSchema,
        attachments: z.array(donationAttachmentSchema),
        /**
         * On the record rather than on `donationSchema`, so a page of a hundred
         * list rows does not read a child table it never renders.
         */
        noticeRecipients: z.array(donationEvidenceRecipientSchema),
        /**
         * The organizations that may take this donation's money: every one its
         * institution is linked to, the primary first. On the record, like the
         * recipients, because a list row never renders them.
         */
        eligibleOrganizations: z.array(donationEligibleOrganizationSchema),
        /**
         * The animals a queued share falls in and how many are carried out —
         * null for a donation in no group.
         */
        animals: donationAnimalsSchema.nullable(),
        /**
         * The caller reads this donation through a campaign staff assignment
         * and through no role (docs/decisions/0090): a screen shows the
         * fulfilment work and leaves out what only a role reads — the money,
         * the receipts, the notices.
         */
        readByAssignment: z.boolean(),
      }),
    ),

  create: oc
    .route({ method: 'POST', path: '/donations' })
    .errors(entered)
    .input(donationCreateInputSchema)
    .output(z.object({ donation: donationSchema })),

  /**
   * A `pending` donation is corrected as a draft once was — its donor, its
   * answers, its parties, its amount, quantity, period and date — and so is a
   * `queued` one, under `donation.editQueued`. Only before money has reached
   * it, and the pledged delta moves in the same transaction as the row. A date
   * is judged by the entry gates, so a day already past needs
   * `donation.backdate` — docs/decisions/0077.
   *
   * The payer is named the two ways an entry names it: a donor the clerk
   * chose, or `payer` — a name and a telephone resolved by 0067's rule inside
   * the correction's own transaction. Never both.
   *
   * `expectedUpdatedAt` is the `updatedAt` the screen was filled from. When it
   * is sent and the row has moved since — somebody else corrected, queued or
   * matched it — the correction is refused 409 `DONATION_CHANGED_SINCE_READ`
   * rather than writing over what they did; absent, nothing is compared, as
   * before — docs/plans/11-test-revisions.md §11.9.
   */
  update: oc
    .route({ method: 'PATCH', path: '/donations/{id}' })
    .errors(entered)
    .input(
      donationEntrySchema
        .partial()
        .extend({
          id: z.uuid(),
          campaignPeriodId: z.uuid().optional(),
          payer: inlinePayerSchema.optional(),
          expectedUpdatedAt: timestampSchema.optional(),
        })
        .refine((input) => input.payer === undefined || input.donorId === undefined, {
          message: 'a correction names the payer as a chosen donor or as a telephone, not both',
          path: ['payer'],
        }),
    )
    .output(z.object({ donation: donationSchema })),

  /**
   * §3.2's *Yetkili kullanıcılar düzenleyebilir* after entry: the operator a
   * donation is recorded against, corrected. Behind
   * `donation.recordForOperator` and `donation.edit`, ceilinged as naming one
   * at entry is, in the statuses an edit reaches, under the queued lock.
   * An optional institution is an explicit historical placement correction.
   * Audited `donation` / `operator_changed` with both operators and placements;
   * retaining both writes nothing — docs/plans/24-donation-recording-attribution.md.
   */
  changeOperator: oc
    .route({ method: 'PUT', path: '/donations/{id}/entering-operator' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        enteredByUserId: z.uuid(),
        institutionId: z.uuid().optional(),
        expectedUpdatedAt: timestampSchema.optional(),
      }),
    )
    .output(z.object({ donation: donationSchema })),

  /**
   * An upper authority's edit of the donation's main organization
   * (docs/decisions/0128). `organizationId: null` hands the choice back to the
   * money. Behind `donation.setMainOrganization`, judged against the donation's
   * scope; the old and new organization and the reason are audited as
   * `donation` / `update`.
   */
  setMainOrganization: oc
    .route({ method: 'PUT', path: '/donations/{id}/main-organization' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        organizationId: z.uuid().nullable(),
        reason: z.string().trim().min(1).max(500),
      }),
    )
    .output(z.object({ donation: donationSchema })),

  /**
   * The organizations `setMainOrganization` would take for this donation: those
   * its institution is linked to and those of movements with a confirmed match
   * on it (docs/decisions/0128 §4). Behind the same permission and scope.
   */
  mainOrganizationOptions: oc
    .route({ method: 'GET', path: '/donations/{id}/main-organization/options' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(
      z.object({
        items: z.array(z.object({ id: z.uuid(), name: z.string() })),
      }),
    ),

  /**
   * The moves this caller may make on this row, after permission, scope and
   * the payment-axis preconditions. The screen renders its buttons from this
   * answer, so no client holds a copy of the state machine that can drift —
   * docs/decisions/0023-donation-status-axes.md.
   */
  transitions: oc
    .route({ method: 'GET', path: '/donations/{id}/transitions' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(
      z.object({
        transitions: z.array(donationTransitionSchema),
        /**
         * Whether `donation.update` would take any correction from this
         * caller — `editability.mode` is not `none`.
         */
        mayEdit: z.boolean(),
        /**
         * How much of the donation `donation.update` would take from this
         * caller, and why it is narrowed: `full`, `nonFinancial` — the figures,
         * the period, the day, the yurt and `lockedAnswerKeys` stay — or
         * `none`. The edit form disables exactly what this names —
         * docs/decisions/0118.
         */
        editability: donationEditabilitySchema,
        /**
         * Whether `donation.changeOperator` would take a change of who the
         * donation is recorded against from this caller: both permissions, a
         * status an edit reaches, and 0071's queued lock — docs/decisions/0070.
         */
        mayChangeOperator: z.boolean(),
        /**
         * Whether `donation.setMainOrganization` would take an edit of the main
         * organization from this caller — the permission, which only the upper
         * roles hold, on a donation inside their scope (docs/decisions/0128).
         */
        mayChangeMainOrganization: z.boolean(),
        /**
         * Whether `donation.delete` would take this donation into the trash
         * from this caller: the permission, `donation.editQueued` beside it on
         * a queued one, and nothing a deletion refuses — docs/decisions/0078.
         */
        mayDelete: z.boolean(),
      }),
    ),

  /**
   * The donation's own trail, behind `donation.view` and scoped exactly as
   * `donation.get` is — so whoever may read the record may read its history.
   * The administrative `audit.list` answers a different question and needs a
   * different grant; a donation's timeline is not an administrative report.
   */
  timeline: oc
    .route({ method: 'GET', path: '/donations/{id}/timeline' })
    .errors(guarded)
    .input(paginationInputSchema.extend({ id: z.uuid() }))
    .output(paginated(auditEntrySchema)),

  /**
   * What `cancel` would do to this donation — a whole cancellation, or only the
   * shares not carried out, with the amounts and the refund — behind the
   * permission the cancel itself needs.
   */
  cancellationPreview: oc
    .route({ method: 'GET', path: '/donations/{id}/cancellation-preview' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(donationCancellationPreviewSchema),

  cancel: oc
    .route({ method: 'POST', path: '/donations/{id}/cancellation' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        reason: reasonSchema,
        paymentOutcome: cancelPaymentOutcomeSchema.optional(),
      }),
    )
    .output(z.object({ donation: donationSchema })),

  /**
   * Allocating a position is one `INSERT … ON CONFLICT DO UPDATE … RETURNING`
   * against `campaign_period_queue_counters` inside the donation's own
   * transaction, so two operators queueing at once take different numbers with
   * no retry and no gap — docs/decisions/0025-queue-allocation-and-derived-grouping.md.
   *
   * Queueing is also where the work is *assigned* a place: the legacy carried
   * `atanan_kesimyeri` on its queue row, and that column is
   * `donations.fulfillment_institution_id` one level up.
   */
  queue: oc
    .route({ method: 'POST', path: '/donations/{id}/queueing' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        fulfillmentInstitutionId: z.uuid().nullish(),
        clientRequestId: clientRequestIdSchema,
      }),
    )
    .output(z.object({ donation: donationSchema })),

  /** `queued → pending`: the position is released, and the counter keeps its number. */
  dequeue: oc
    .route({ method: 'POST', path: '/donations/{id}/dequeueing' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ donation: donationSchema })),

  /**
   * Where and when it was carried out, and what it cost in its own currency.
   * `queue_position` is **kept**: the queue is the record of what was executed
   * in what order, and clearing it would destroy the group sheet already handed
   * to a field team.
   */
  fulfill: oc
    .route({ method: 'POST', path: '/donations/{id}/fulfillment' })
    .errors(guarded)
    .input(fulfillmentInputSchema.extend({ id: z.uuid(), clientRequestId: clientRequestIdSchema }))
    .output(z.object({ donation: donationSchema })),

  /**
   * A distribution run: dozens of records completed together at one place, on
   * one day, at one cost each. It is a bulk endpoint, so it is **one
   * transaction per row** with its own audit row — one refusal inside the run
   * does not roll back the records already completed
   * (docs/decisions/0026-donation-approval.md). `fulfillGroup` answers a
   * different question: the shares of one derived animal, resolved from the
   * period's order rather than named by the caller.
   */
  bulkFulfill: oc
    .route({ method: 'POST', path: '/donations/fulfillments' })
    .errors(guarded)
    .input(fulfillmentInputSchema.extend({ items: bulkFulfilmentItemsSchema }))
    .output(bulkResultSchema(donationBulkResultSchema)),

  /** Up to 500 ids, a result per row, and never a failed batch. */
  bulkQueue: oc
    .route({ method: 'POST', path: '/donations/queueings' })
    .errors(guarded)
    .input(z.object({ ids: bulkIdsSchema, fulfillmentInstitutionId: z.uuid().nullish() }))
    .output(bulkResultSchema(donationBulkResultSchema)),

  /**
   * `queued → pending` for a selection, back to *Bekleyenler*: `bulkQueue`'s
   * shape the other way — up to 500 ids, one transaction and one audit row per
   * row, a result per row — docs/decisions/0077.
   */
  bulkDequeue: oc
    .route({ method: 'POST', path: '/donations/dequeueings' })
    .errors(guarded)
    .input(z.object({ ids: bulkIdsSchema }))
    .output(bulkResultSchema(donationBulkResultSchema)),

  /**
   * Into the trash: a `pending` or `queued` donation nothing has reached —
   * no money, match, receipt, open payment link, printed page, carried-out
   * share, evidence or box emptying — is marked deleted and kept, its pledge
   * off the period's totals in the same transaction, and a queued one gives
   * its place in the order up. A `fulfilled` one is cancelled instead —
   * docs/decisions/0078-a-deleted-donation-is-kept-and-restored.md.
   */
  delete: oc
    .route({ method: 'POST', path: '/donations/{id}/deletion' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), reason: deletionReasonSchema }))
    .output(z.object({ id: z.uuid() })),

  /**
   * The same for a selection: one transaction and one audit row per row, a
   * result per row, and one reason and one `bulkOperationId` for every row it
   * deletes — docs/decisions/0085.
   */
  bulkDelete: oc
    .route({ method: 'POST', path: '/donations/deletions' })
    .errors(guarded)
    .input(z.object({ ids: bulkIdsSchema, reason: deletionReasonSchema }))
    .output(donationDeletionBulkResponseSchema),

  /**
   * The trash: the caller's deleted donations, behind
   * `donation.viewDeleted` and the donation scope, newest deletion first.
   */
  listDeleted: oc
    .route({ method: 'GET', path: '/deleted-donations' })
    .errors(guarded)
    .input(deletedDonationListInputSchema)
    .output(paginated(deletedDonationSchema)),

  /**
   * One deleted donation's page, read-only, behind `donation.viewDeleted` and
   * the trash's scope: a live donation, or one outside the caller's subtree,
   * is missing here as a deleted one is on `get` — docs/decisions/0085.
   */
  getDeleted: oc
    .route({ method: 'GET', path: '/deleted-donations/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(deletedDonationDetailSchema),

  /** The deleted donation's own trail, scoped and permissioned as `getDeleted` is. */
  deletedTimeline: oc
    .route({ method: 'GET', path: '/deleted-donations/{id}/timeline' })
    .errors(guarded)
    .input(paginationInputSchema.extend({ id: z.uuid() }))
    .output(paginated(auditEntrySchema)),

  /**
   * Out of the trash, in the status it held: the pledge back on the period's
   * totals and, for a queued one, a place at the end of the order. Refused
   * once its season is closed or its yurt's assignment withdrawn.
   */
  restore: oc
    .route({ method: 'POST', path: '/donations/{id}/restoration' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ donation: donationSchema })),

  /**
   * The ordered list of a period's live shares with the grouping derived at
   * read time. It is one screen for every campaign: nothing here branches on
   * which campaign it is, and a campaign with no `units_per_group` collapses to
   * a flat list.
   */
  queueList: oc
    .route({ method: 'GET', path: '/campaign-periods/{campaignPeriodId}/queue' })
    .errors(guarded)
    .input(z.object({ campaignPeriodId: z.uuid() }))
    .output(z.object({ period: queuePeriodSchema, items: z.array(queueEntrySchema) })),

  /**
   * A drag sends the **full ordered `id[]`** for the period and the positions
   * are rewritten in one transaction under `SET CONSTRAINTS … DEFERRED`, never
   * a pair of swaps. An incomplete list is refused rather than interleaved.
   */
  queueReorder: oc
    .route({ method: 'POST', path: '/campaign-periods/{campaignPeriodId}/queue/order' })
    .errors(guarded)
    .input(z.object({ campaignPeriodId: z.uuid(), ids: z.array(z.uuid()).min(1).max(2000) }))
    .output(z.object({ period: queuePeriodSchema, items: z.array(queueEntrySchema) })),

  /**
   * The homogeneity the legacy's five-level cascading top-up was reaching for,
   * expressed as an ordering: grouping follows the order, so ordering by the
   * institution that will carry the work out makes the groups belong to one
   * place by construction.
   *
   * Besides the four fixed keys, `answer:<field key>` sorts by one of the
   * campaign's grouping answers — §4.3's operator criteria — and a key the
   * campaign does not group by is refused `DONATION_QUEUE_SORT_FIELD_UNKNOWN`.
   * On a campaign that groups by answers a sort moves whole runs of agreeing
   * shares, never a share out of its animal.
   */
  queueSort: oc
    .route({ method: 'POST', path: '/campaign-periods/{campaignPeriodId}/queue/sorting' })
    .errors(guarded)
    .input(z.object({ campaignPeriodId: z.uuid(), sortKey: queueSortKeySchema }))
    .output(z.object({ period: queuePeriodSchema, items: z.array(queueEntrySchema) })),

  /**
   * The legacy's descending first-fit, written as a **permutation** of
   * `queue_position` rather than as a set of group rows. It creates nothing,
   * numbers nothing, drops nothing and reports no row as unassignable.
   */
  queueCompact: oc
    .route({ method: 'POST', path: '/campaign-periods/{campaignPeriodId}/queue/compaction' })
    .errors(guarded)
    .input(z.object({ campaignPeriodId: z.uuid() }))
    .output(z.object({ period: queuePeriodSchema, items: z.array(queueEntrySchema) })),

  /**
   * §4.3's grouping engine, the operator's *Grupla*: the live shares gathered
   * by the planned yurt and the campaign's grouping answers, each gathering
   * packed by the compaction's first-fit, and the result written as one
   * permutation under the period's lock. Every group's number is then its
   * *kesim sıra no*. Refused `DONATION_QUEUE_LOCKED` once a share is fulfilled.
   */
  queueGroup: oc
    .route({ method: 'POST', path: '/campaign-periods/{campaignPeriodId}/queue/grouping' })
    .errors(guarded)
    .input(z.object({ campaignPeriodId: z.uuid() }))
    .output(z.object({ period: queuePeriodSchema, items: z.array(queueEntrySchema) })),

  /**
   * The legacy's one-video-seven-rows cascade as an explicit operator action
   * rather than a side effect of an S3 upload finishing. The group is resolved
   * from the order under the period's lock and recorded as one animal, in one
   * transaction; each donation holding shares in it becomes `fulfilled` only if
   * this was the last of its animals. A group already recorded is 409
   * `DONATION_GROUP_ALREADY_FULFILLED`, and a share outside the caller's act
   * scope refuses the whole animal.
   */
  fulfillGroup: oc
    .route({
      method: 'POST',
      path: '/campaign-periods/{campaignPeriodId}/queue/groups/{groupIndex}/fulfillment',
    })
    .errors(guarded)
    .input(
      fulfillmentInputSchema.extend({
        campaignPeriodId: z.uuid(),
        groupIndex: z.coerce.number().int().min(1),
      }),
    )
    .output(bulkResultSchema(queueGroupFulfilmentResultSchema)),

  /**
   * Evidence rides the ordinary `attachments` table, and **attaching it never
   * changes a status**: the legacy made a completed upload the fulfillment
   * record, so a storage failure left donations permanently unfinished.
   *
   * The presigned-upload, byte-sniffing and virus-scan pipeline is Phase 7's
   * (.claude/rules/file-uploads.md), so the object key is minted here and
   * returned for that pipeline to upload against; until it lands nothing puts
   * bytes behind the key and `scan_status` stays `pending`.
   */
  /**
   * Step one of the three §5.4's statement import established and the campaign
   * picture copied: the server mints the key, signs a `PUT` for exactly this
   * many bytes and hands back a token naming what it issued. Nothing is written
   * yet, and the browser never chooses a key.
   */
  requestAttachmentUpload: oc
    .route({ method: 'POST', path: '/donations/{id}/attachment-uploads' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        fileName: fileNameSchema,
        fileSize: z.number().int().min(1).max(DONATION_EVIDENCE_MAX_BYTES),
      }),
    )
    .output(presignedUploadSchema),

  /**
   * Step three. The token is checked, the object is read back and **the bytes
   * decide what it is**, then it is written to a second key only the server
   * chose — so what the presigned URL wrote can never be what is served. The
   * key is not returned any more: nothing outside the server needs it.
   *
   * **Attaching evidence never changes a status.** The legacy made a completed
   * upload the fulfillment record, so a storage failure left donations
   * permanently unfinished and a retry silently completed them.
   */
  attachmentLink: oc
    .route({ method: 'POST', path: '/donations/{id}/attachments' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), uploadToken: uploadTokenSchema }))
    .output(z.object({ attachment: donationAttachmentSchema })),

  /**
   * A 60-second presigned `GET`, issued only for a file scanned clean and not
   * deleted, and audited before the link is returned whether or not it is
   * followed. Judged as fulfilment is, like attaching and unlinking.
   */
  attachmentDownload: oc
    .route({ method: 'GET', path: '/donations/{id}/attachments/{attachmentId}/link' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), attachmentId: z.uuid() }))
    .output(
      z.object({
        url: z.string(),
        contentType: z.string(),
        expiresInSeconds: z.number().int().positive(),
      }),
    ),

  /**
   * The download of a file the campaign's own form captured. Judged as
   * **reading** the donation is — `donation.view` against the donation's scope
   * — because the document is part of the answers the donation carries, not
   * part of carrying the work out, which is what `donation.fulfill` governs for
   * fulfilment evidence. A 60-second presigned `GET`, issued only for a file
   * scanned clean and not deleted, audited before the link is returned.
   */
  formFileDownload: oc
    .route({ method: 'GET', path: '/donations/{id}/form-files/{attachmentId}/link' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), attachmentId: z.uuid() }))
    .output(
      z.object({
        url: z.string(),
        contentType: z.string(),
        expiresInSeconds: z.number().int().positive(),
      }),
    ),

  /**
   * Every file linked to the donation, for the detail page's *Medya / Kanıt
   * Dosyaları* area: its evidence captures, the field media matched to it, the
   * files its form captured and its receipt PDFs. Behind `donation.view` and
   * read through the donation's scope as `get` is — a donation outside it is
   * `NOT_FOUND`. Nothing here signs a link: each file is opened through its own
   * module's audited route, which `mayOpen` mirrors — docs/decisions/0118.
   */
  media: oc
    .route({ method: 'GET', path: '/donations/{id}/media' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ items: z.array(donationMediaItemSchema) })),

  attachmentList: oc
    .route({ method: 'GET', path: '/donations/{id}/attachments' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ attachments: z.array(donationAttachmentSchema) })),

  attachmentUnlink: oc
    .route({ method: 'DELETE', path: '/donations/{id}/attachments/{attachmentId}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), attachmentId: z.uuid() }))
    .output(z.object({ id: z.uuid() })),

  /**
   * Who a donation's evidence is for and where each party's link stands, for
   * the donation page's evidence card. Behind `donation.fulfill`, the
   * permission that governs a donation's evidence and its resend, and read
   * through the caller's donation scope exactly as `get` is — a record outside
   * it is `NOT_FOUND`. At most one row per party, so it is not paginated.
   */
  evidenceRecipients: oc
    .route({ method: 'GET', path: '/donations/{id}/evidence-recipients' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ items: z.array(donationEvidenceDeliverySchema) })),

  /**
   * *Bildirim İşlemleri* on the donation page: the four parties, what each is
   * told, and whether each may be ticked — docs/decisions/0080. Read through
   * the caller's donation scope exactly as `get` is, behind `donation.view`.
   */
  noticeChoices: oc
    .route({ method: 'GET', path: '/donations/{id}/notice-choices' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(donationNoticeChoicesSchema),

  /**
   * Who is told the donation was carried out — the *GERÇEKLEŞTİ (GÖRSEL/KANIT)
   * BİLDİRİMİ* column, as the whole set of ticked parties. A party added is
   * declared by the caller and, on a donation already carried out with
   * evidence, offered the link at once; a party taken away is refused once its
   * link was offered (`DONATION_NOTICE_CHOICE_OFFERED` — the opt-out stops it
   * instead); a party who opted out stays as they are. The registration notice
   * went out at the save and is not changed here. Behind `donation.edit`, and
   * audited `donation` / `notice_choices_changed`.
   */
  setNoticeChoices: oc
    .route({ method: 'PUT', path: '/donations/{id}/notice-choices' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        fulfilment: z.array(donationEvidenceRecipientRoleSchema).max(4),
      }),
    )
    .output(donationNoticeChoicesSchema),

  /**
   * A party who asked for the evidence and no longer wants it — most often the
   * reference person, who never agreed to anything themselves and whose basis
   * is the operator's declaration. Withdrawing must be no easier to refuse than
   * the tick that asked, so it is one call, behind the permission that edits
   * the record, and it keeps the row: *when did they stop* is a fact.
   * Repeating it changes nothing — docs/decisions/0070.
   */
  evidenceOptOut: oc
    .route({ method: 'POST', path: '/donations/{id}/evidence-recipients/{role}/opt-out' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), role: donationEvidenceRecipientRoleSchema }))
    .output(z.object({ noticeRecipients: z.array(donationEvidenceRecipientSchema) })),

  /**
   * *Kanıt bağlantısını yeniden gönder* — one party's evidence link sent
   * again, for a message whose outcome nobody can know: an SMS or WhatsApp
   * message that timed out is never sent twice by the delivery itself. It
   * clears whatever token the link held, so every earlier one stops opening,
   * and raises a fresh message through the offer's own path; the token is
   * minted when that message leaves. Behind `donation.fulfill`, the permission
   * that governs a donation's evidence. Refused for a party who opted out, an
   * erased donor, a donation with no evidence to show and a message still on
   * its way — docs/decisions/0075-evidence-travels-as-an-expiring-link.md.
   */
  evidenceResend: oc
    .route({ method: 'POST', path: '/donations/{id}/evidence-recipients/{role}/resend' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), role: donationEvidenceRecipientRoleSchema }))
    .output(z.object({ queued: z.number().int().nonnegative() })),
};
